import type { BootEnv } from "../boot/createBootConfig";

/**
 * On-device console for staging builds — the deploy guide's vConsole snippet, folded into the
 * boot (`launchGame`). Build-gated on the title's `VITE_VCONSOLE=1` (`.env.staging`): the lazy chunk
 * is in every build since decision 40 but fetched only by a flagged one. Run-gated on `?vconsole=1`,
 * which also pins it in localStorage for the next boots. `?vconsole=0` clears the pin. Runs before
 * `startGame` so the boot log is captured — and so before the boot boundary exists: an install that
 * fails (the chunk does not load, or the browser refuses `new VConsole`'s eval) is warned and skipped,
 * and the game boots without the console. The pin stays; the next boot tries again (review 2026-09-27 #30).
 */
const PIN_KEY = "vconsole-pinned";

export async function installVConsole(input: { env: BootEnv; search: string }): Promise<void> {
  if (input.env.VITE_VCONSOLE !== "1") return;
  const flag = new URLSearchParams(input.search).get("vconsole");
  if (flag === "0") {
    try {
      localStorage.removeItem(PIN_KEY);
    } catch {
      /* storage blocked */
    }
    return;
  }
  let pinned = false;
  try {
    pinned = localStorage.getItem(PIN_KEY) === "1";
  } catch {
    /* storage blocked */
  }
  if (flag !== "1" && !pinned) return;
  try {
    localStorage.setItem(PIN_KEY, "1");
  } catch {
    /* storage blocked */
  }
  try {
    const { default: VConsole } = await import("vconsole");
    new VConsole({ theme: "dark" });
  } catch (error) {
    console.warn("[vConsole] not installed; the game boots without it", error);
    return;
  }
  console.log("[vConsole] installed (?vconsole=0 removes the pin)");
}
